import { useState, useEffect } from "react";
import { FaArrowUp } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";

import {
  searchAdsByReference,
  updateVideoSection,
} from "../../../api/webData.api";

import "./VideoSectionEditForm.scss";

// --- HELPER PARA LIMPIAR IDs ---
// Esto asegura que siempre obtengamos un string limpio, venga como venga el dato
const getSafeId = (id) => {
  if (!id) return `temp-${Math.random().toString(36).substr(2, 9)}`; // ID temporal si viene vacío

  if (typeof id === "string") return id;

  // Si es un objeto (Mongoose ObjectId, etc)
  if (id && typeof id.toString === "function") {
    const stringified = id.toString();
    if (stringified === "[object Object]") {
      // Si tiene propiedad _id o adId dentro, úsala
      if (id._id) return getSafeId(id._id);
      if (id.adId) return getSafeId(id.adId);
      return JSON.stringify(id);
    }
    return stringified;
  }

  return String(id);
};

const VideoSectionEditForm = ({
  setOpenEditVideoSectionForm,
  webData,
  setWebData,
  setHideContent,
}) => {
  const [title, setTitle] = useState(webData?.videoSection?.title || "");
  const [subtitle, setSubtitle] = useState(
    webData?.videoSection?.subtitle || "",
  );

  const [selectedAds, setSelectedAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // --- 1. CARGA INICIAL ROBUSTA ---
  useEffect(() => {
    if (webData?.videoSection?.videos?.length > 0) {
      const existingData = webData.videoSection.videos.map((v) => {
        // Prioridad 1: Objeto con adId
        if (typeof v === "object" && v.adId) {
          return {
            ...v,
            _id: getSafeId(v.adId), // Usamos el helper
          };
        }
        // Prioridad 2: Objeto con _id (legacy o roto)
        if (typeof v === "object" && v._id) {
          return {
            ...v,
            _id: getSafeId(v._id),
          };
        }
        // Prioridad 3: String directo
        return {
          _id: getSafeId(v),
          adReference: "Sin ref",
          title: "Dato antiguo",
        };
      });

      // FILTRO DE SEGURIDAD: Eliminamos duplicados si vinieran mal de la BD
      const uniqueAds = existingData.filter(
        (ad, index, self) => index === self.findIndex((t) => t._id === ad._id),
      );

      setSelectedAds(uniqueAds);
    }
  }, [webData]);

  // --- 2. BÚSQUEDA ---
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      const results = await searchAdsByReference(searchTerm);

      // Filtramos lo que ya está en la lista para no sugerirlo
      const currentIds = selectedAds.map((a) => a._id);

      const availableResults = results.filter((ad) => {
        const resultId = getSafeId(ad._id);
        return !currentIds.includes(resultId);
      });

      setSearchResults(availableResults);
      setIsSearching(false);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedAds]);

  // --- 3. SELECCIONAR (CON PROTECCIÓN DE DUPLICADOS) ---
  const handleSelectAd = (ad) => {
    const newId = getSafeId(ad._id);

    // SEGURIDAD EXTRA: Si por alguna razón ya existe, no lo añadimos
    const alreadyExists = selectedAds.some((item) => item._id === newId);
    if (alreadyExists) {
      setSearchTerm("");
      setSearchResults([]);
      return;
    }

    const newAdObj = { ...ad, _id: newId };
    setSelectedAds([...selectedAds, newAdObj]);
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleRemoveId = (idToRemove) => {
    const safeIdToRemove = getSafeId(idToRemove);
    const updatedList = selectedAds.filter((ad) => ad._id !== safeIdToRemove);
    setSelectedAds(updatedList);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newList = [...selectedAds];
    // Intercambio simple
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setSelectedAds(newList);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Enviamos solo strings limpios
    const idsToSend = selectedAds.map((ad) => ad._id);

    const payload = {
      title,
      subtitle,
      selectedAdIds: idsToSend,
    };

    try {
      const response = await updateVideoSection(webData._id, payload);
      if (response) {
        setWebData(response);
        setOpenEditVideoSectionForm(false);
        setHideContent(false);
      }
    } catch (error) {
      console.error(error);
      alert("Error al guardar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="editFormContainer">
      <h2>Editar Sección de Videos</h2>
      <form onSubmit={handleSubmit} className="editForm">
        <div className="form-group">
          <label>Título:</label>
          <input
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtítulo:</label>
          <input
            className="form-control"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Añadir Videos por Referencia:</label>

          <div style={{ position: "relative" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar referencia... "
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isSearching && <span className="searchingLabel">Buscando...</span>}

            {searchResults.length > 0 && (
              <ul className="suggestionsUl">
                {searchResults.map((ad) => (
                  <li
                    // KEY SEGURA
                    key={getSafeId(ad._id)}
                    onClick={() => handleSelectAd(ad)}
                    className="suggestionsUl__li"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f0f0f0")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "white")
                    }
                  >
                    <span style={{ fontWeight: "600" }}>{ad.adReference}</span>
                    <span>{ad.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="selectedContainer">
            <label>Videos Seleccionados ({selectedAds.length})</label>
            {selectedAds.length === 0 ? (
              <p>No hay videos seleccionados</p>
            ) : (
              <ul className="selectedUl">
                {selectedAds.map((ad, index) => {
                  const uniqueKey = ad._id
                    ? getSafeId(ad._id)
                    : `index-${index}`;

                  return (
                    <li
                      // KEY SEGURA: Esto soluciona el error de React
                      key={uniqueKey}
                      className="selectedLi"
                    >
                      <div className="selectedLi__textContainer">
                        <span style={{ fontWeight: "600" }}>
                          #{index + 1} - {ad.adReference || "Sin Ref"}
                        </span>
                        <span>{ad.title || "Sin título"}</span>
                        {/* Debug opcional: ver el ID real */}
                        {/* <small style={{fontSize: '10px'}}>{ad._id}</small> */}
                      </div>

                      <div className="selectedLi__buttonContainer">
                        <button
                          type="button"
                          className="selectedLi__buttonContainer__up"
                          onClick={() => moveUp(index)}
                        >
                          <FaArrowUp size={22} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveId(ad._id)}
                          className="selectedLi__buttonContainer__close"
                        >
                          <IoIosCloseCircleOutline size={30} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="buttons-container">
          <button
            type="button"
            className="buttons-container-button"
            onClick={() => {
              setOpenEditVideoSectionForm(false);
              setHideContent(false);
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="buttons-container-button"
            disabled={loading}
          >
            {loading ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VideoSectionEditForm;
